import { watch } from 'vue'
import { pusherService } from '@/services/pusherService'
import { getSocketId } from '@/services/socketId'
import vuex from '@/store'

/**
 * Keeps the media asset list current from the API's media asset broadcasts,
 * replacing the /changes poll as the primary update path.
 *
 * One subscription per app, not per page: state.assets is global and both the
 * layout and the Media Assets page read it, so a per-component subscription
 * would apply every event once per open page.
 *
 * Nothing here fetches on receipt. The payload is applied straight to the
 * store — a fetch per event would turn one upload into a read from every open
 * tab in the station, which is the cost this exists to remove.
 */
class MediaStream {
  constructor() {
    this.cleanupFns = []
    this.stopWatchers = []
    // The station we are bound to right now, and the last one we were ever
    // bound to. The difference is what tells a reconnect from a station switch.
    this.boundStation = null
    this.lastBoundStation = null
  }

  /**
   * Called once at app start. Idempotent — the watchers it installs drive
   * everything after that.
   */
  start() {
    if (this.stopWatchers.length) return

    this.stopWatchers.push(
      watch(
        () => vuex.getters.currentStation?.station,
        () => this.sync()
      ),
      watch(
        () => pusherService.isConnected.value,
        () => this.sync()
      )
    )

    this.sync()
  }

  stop() {
    this.unbind()
    this.lastBoundStation = null

    this.stopWatchers.forEach((stop) => stop())
    this.stopWatchers = []
  }

  /**
   * Reconcile the subscription with the connection and the station. Safe to
   * call as often as either changes; it no-ops when already correct.
   */
  sync() {
    const station = vuex.getters.currentStation?.station
    const want = pusherService.isConnected.value && station ? station : null

    if (want === this.boundStation) return

    // Rebinding the same station means we lost the socket and got it back. An
    // API deploy drops every connection, and anything published while we were
    // down is gone — nothing replays it, so pull once rather than leaving the
    // list stale until the next poll tick.
    const isReconnect = want !== null && want === this.lastBoundStation

    this.unbind()

    if (!want) return

    this.bind(want)

    if (isReconnect) this.resync()
  }

  bind(station) {
    // The private- prefix is what makes Reverb authorize the subscribe; without
    // it this is a different, public channel that nothing publishes to.
    const channel = `private-media-assets.${station}`

    pusherService.subscribe(channel)

    for (const action of ['created', 'updated', 'deleted']) {
      this.cleanupFns.push(pusherService.on(channel, `media_asset.${action}`, (event) => this.handle(event)))
    }

    this.boundStation = station
    this.lastBoundStation = station
    console.debug('[MediaStream] bound to', channel)
  }

  unbind() {
    this.cleanupFns.forEach((fn) => fn())
    this.cleanupFns = []

    if (this.boundStation) {
      pusherService.unsubscribe(`private-media-assets.${this.boundStation}`)
      this.boundStation = null
    }
  }

  handle(event) {
    // A subscription torn down mid-switch can still deliver one more event;
    // applying it would put another station's asset in this station's list.
    if (event?.station_name !== vuex.getters.currentStation?.station) return

    // Our own write coming back. The API leaves this socket out of the publish
    // where it can, but Reverb only honours that on the instance that received
    // it, so behind more than one instance the echo usually gets through. The
    // writer already has the save response; applying the echo on top of it
    // would overwrite what the user is looking at with an older copy. It still
    // moves the watermark, or the next poll would reload the list for a change
    // this tab made itself.
    if (event.origin_socket_id && event.origin_socket_id === getSocketId()) {
      vuex.dispatch('advanceMediaWatermark', event)
      return
    }

    vuex.dispatch('applyMediaBroadcast', event)
  }

  /**
   * Off the watermark rather than a straight refetch: it compares the station's
   * watermark against what we have already applied, so a reconnect after a
   * quiet minute costs one /changes row and no list fetch at all. When it does
   * find a change, checkMediaChanges announces it, and the Media Assets page
   * refreshes its own server-side rows from that.
   */
  resync() {
    vuex.dispatch('getMediaChanges').catch((error) => {
      console.error('[MediaStream] resync after reconnect failed', error)
    })
  }
}

export const mediaStream = new MediaStream()

// The /changes media poll follows the socket, as the story list's does (ND-2380).
// With it up, broadcasts keep the list current and the poll is a backstop for
// anything they missed; with it down, the poll is the update path.
const BACKSTOP_POLL_SECONDS = 300
const FALLBACK_POLL_SECONDS = 60

/**
 * Seconds between media /changes polls. Reads the connection reactively, so a
 * computed built on it changes when the socket drops or comes back.
 */
export function mediaPollSeconds() {
  return pusherService.isConnected.value ? BACKSTOP_POLL_SECONDS : FALLBACK_POLL_SECONDS
}
